import apiClient from "../../apis/tenant-management/tenant-management-client";
import { toast } from "react-toastify";
import { Organization } from "../../domain/organization/organization";
import { BaseApplicationService } from "../../domain/base/base-application-service";
import { Option as Opt, some, none } from "fp-ts/lib/Option";
import { reportUnexpectedError } from "../../../error-reporter";
import { OrgWithUsingFeatures } from "../../domain/organization/org-with-using-features";

export type OrganizationBasicInfoServiceProps = {
  isLoading: boolean;
  organizations: Array<Organization>;
  selectedOrgWithUsingFeatures: Opt<OrgWithUsingFeatures>;
  fetchOrganizations: typeof OrganizationBasicInfoService.prototype.fetchOrganizations;
  fetchOrganization: typeof OrganizationBasicInfoService.prototype.fetchOrganization;
  onCreateOrganization: typeof OrganizationBasicInfoService.prototype.onCreateOrganization;
  onUpdateOrganization: typeof OrganizationBasicInfoService.prototype.onUpdateOrganization;
};

export class OrganizationBasicInfoService extends BaseApplicationService<OrganizationBasicInfoServiceProps> {
  state = {
    isLoading: false,
    organizations: [],
    selectedOrgWithUsingFeatures: none,
    fetchOrganizations: this.fetchOrganizations.bind(this),
    fetchOrganization: this.fetchOrganization.bind(this),
    onCreateOrganization: this.onCreateOrganization.bind(this),
    onUpdateOrganization: this.onUpdateOrganization.bind(this),
  };

  // org-basic-info
  async onUpdateOrganization(formData: Organization) {
    await this.updateOrganization(formData);
    await this.fetchOrganization(formData.id!);
  }

  async onCreateOrganization(formData: Organization) {
    await this.createOrganization(formData);
    await this.fetchOrganizations();
  }

  async fetchOrganizations() {
    this.setIsLoading(true);
    try {
      const { data: organizations }: { data: Array<Organization> } =
        await apiClient.fetchOrganizations();
      this.setState({
        organizations: organizations,
      });
    } catch (e) {
      if (e.response.data.code === "not_found") {
        return;
      }
      reportUnexpectedError(e);
      toast.error("テナント一覧の取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  async fetchOrganization(orgId: string) {
    this.setIsLoading(true);
    try {
      const { data: orgWithUsingFeature }: { data: OrgWithUsingFeatures } =
        await apiClient.fetchOrgWithUsingFeature(orgId);
      this.setState({
        selectedOrgWithUsingFeatures: some(orgWithUsingFeature),
      });
    } catch (e) {
      if (e.response.data.code === "not_found") {
        return;
      }
      reportUnexpectedError(e);
      toast.error("テナントの取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  private async createOrganization(formData: Organization) {
    this.setIsLoading(true);
    try {
      await apiClient.createOrganization(formData);
      toast.success("テナントの追加に成功しました");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("新規作成に失敗しました");
    } finally {
      this.setIsLoading(false);
    }
  }

  private async updateOrganization(formData: Organization) {
    this.setIsLoading(true);
    try {
      await apiClient.updateOrganization(formData);
      toast.success("テナントの更新に成功しました");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("更新に失敗しました");
    } finally {
      this.setIsLoading(false);
    }
  }

  private setIsLoading(isLoading: boolean) {
    this.setState({
      isLoading: isLoading,
    });
  }
}
